import React, { useState, useRef } from 'react';
import { Upload, Image, MessageSquare, Twitter, Link, Globe, Eye, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ImagePicker } from '@/components/ImagePicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Character limits for OG fields
const TITLE_MAX = 70;
const DESCRIPTION_MAX = 200;

/**
 * SocialPreviewSettings Component
 * 
 * A reusable component for configuring Open Graph and Twitter Card metadata.
 * Used in ShortURL, QRCode, LandingPage, and BioPage forms.
 * 
 * Features:
 * - Title and description inputs with character counters
 * - OG image upload with automatic processing (1200x630)
 * - Twitter card type selection
 * - Live preview simulator for WhatsApp, LinkedIn, and Twitter
 * - Option to use destination site's preview instead
 */
export default function SocialPreviewSettings({ 
  value = {}, 
  onChange,
  assetName = 'Link',
  destinationUrl = ''
}) {
  const [previewTab, setPreviewTab] = useState('whatsapp');

  // Local state for form values
  const socialPreview = {
    og_title: value?.og_title || '',
    og_description: value?.og_description || '',
    og_image_url: value?.og_image_url || '',
    og_image_id: value?.og_image_id || '',
    og_canonical_url: value?.og_canonical_url || '',
    twitter_card_type: value?.twitter_card_type || 'summary_large_image',
    use_destination_preview: value?.use_destination_preview || false,
  };

  const handleChange = (field, newValue) => {
    onChange({
      ...socialPreview,
      [field]: newValue
    });
  };

  const handleImageSelect = (url, fallbackUrl) => {
    // Update both the URL and ID (we can use the URL as ID for now)
    handleChange('og_image_url', url || '');
    handleChange('og_image_id', url || '');
  };

  // Get display values with fallbacks
  const displayTitle = socialPreview.og_title || assetName || 'Your Link Title';
  const displayDescription = socialPreview.og_description || 'A brief description of your link will appear here.';
  const displayUrl = destinationUrl ? (() => { try { return new URL(destinationUrl).hostname; } catch { return 'your-domain.com'; } })() : 'your-domain.com';

  // Build the full image URL for display
  const getDisplayImageUrl = () => {
    if (!socialPreview.og_image_url) return null;
    // If it's already a full URL, use it as-is
    if (socialPreview.og_image_url.startsWith('http')) {
      return socialPreview.og_image_url;
    }
    // Otherwise prepend the backend URL
    return `${BACKEND_URL}${socialPreview.og_image_url}`;
  };

  const displayImageUrl = getDisplayImageUrl();

  // If using destination preview, show disabled state
  if (socialPreview.use_destination_preview) {
    return (
      <Card data-testid="social-preview-settings">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Social Preview Settings
          </CardTitle>
          <CardDescription>
            Configure how your link appears when shared on social media
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div className="space-y-1">
              <Label className="text-base">Use Destination Site's Preview</Label>
              <p className="text-sm text-muted-foreground">
                When enabled, social platforms will fetch the preview from the destination URL instead of using custom settings.
              </p>
            </div>
            <Switch
              data-testid="use-destination-preview-toggle"
              checked={socialPreview.use_destination_preview}
              onCheckedChange={(checked) => handleChange('use_destination_preview', checked)}
            />
          </div>
          
          <div className="p-4 border rounded-lg bg-muted/50">
            <p className="text-sm text-muted-foreground">
              Custom preview settings are disabled. Social platforms will use the Open Graph tags from:{' '}
              <span className="font-medium">{destinationUrl || 'the destination URL'}</span>
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="social-preview-settings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="w-5 h-5" />
          Social Preview Settings
        </CardTitle>
        <CardDescription>
          Configure how your link appears when shared on social media
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Toggle for using destination preview */}
        <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
          <div className="space-y-1">
            <Label className="text-base">Use Destination Site's Preview</Label>
            <p className="text-sm text-muted-foreground">
              Let social platforms fetch preview from the destination URL
            </p>
          </div>
          <Switch
            data-testid="use-destination-preview-toggle"
            checked={socialPreview.use_destination_preview}
            onCheckedChange={(checked) => handleChange('use_destination_preview', checked)}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Settings Column */}
          <div className="space-y-4">
            {/* OG Title */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="og_title">Preview Title</Label>
                <span className={`text-xs ${socialPreview.og_title.length > TITLE_MAX ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {socialPreview.og_title.length}/{TITLE_MAX}
                </span>
              </div>
              <Input
                id="og_title"
                data-testid="og-title-input"
                placeholder={assetName || "Enter preview title"}
                value={socialPreview.og_title}
                onChange={(e) => handleChange('og_title', e.target.value)}
                maxLength={TITLE_MAX + 10} // Allow slight overflow with warning
              />
              <p className="text-xs text-muted-foreground">
                Appears as the main headline in social shares
              </p>
            </div>

            {/* OG Description */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="og_description">Preview Description</Label>
                <span className={`text-xs ${socialPreview.og_description.length > DESCRIPTION_MAX ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {socialPreview.og_description.length}/{DESCRIPTION_MAX}
                </span>
              </div>
              <Textarea
                id="og_description"
                data-testid="og-description-input"
                placeholder="Enter a brief description for social shares"
                value={socialPreview.og_description}
                onChange={(e) => handleChange('og_description', e.target.value)}
                rows={3}
                maxLength={DESCRIPTION_MAX + 20}
              />
            </div>

            {/* OG Image Upload - Using ImagePicker */}
            <div className="space-y-2">
              <Label>Preview Image</Label>
              <ImagePicker
                value={socialPreview.og_image_url}
                onChange={handleImageSelect}
                placeholder="Select or upload an image for social previews"
                aspectRatio="1200/630"
                folder="og_images"
                testId="og-image-picker"
              />
              <p className="text-xs text-muted-foreground">
                Recommended size: 1200×630 pixels. Images will be automatically resized.
              </p>
            </div>

            {/* Twitter Card Type */}
            <div className="space-y-2">
              <Label htmlFor="twitter_card_type">Twitter Card Type</Label>
              <Select
                value={socialPreview.twitter_card_type}
                onValueChange={(val) => handleChange('twitter_card_type', val)}
              >
                <SelectTrigger data-testid="twitter-card-type-select">
                  <SelectValue placeholder="Select card type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="summary_large_image">
                    <div className="flex items-center gap-2">
                      <Image className="w-4 h-4" />
                      Large Image Card
                    </div>
                  </SelectItem>
                  <SelectItem value="summary">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Summary Card
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Large image cards show a bigger preview image
              </p>
            </div>
          </div>

          {/* Preview Column */}
          <div className="space-y-4">
            <Label className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Live Preview
            </Label>
            
            <Tabs value={previewTab} onValueChange={setPreviewTab}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="whatsapp" data-testid="preview-tab-whatsapp">WhatsApp</TabsTrigger>
                <TabsTrigger value="linkedin" data-testid="preview-tab-linkedin">LinkedIn</TabsTrigger>
                <TabsTrigger value="twitter" data-testid="preview-tab-twitter">Twitter</TabsTrigger>
              </TabsList>

              <TabsContent value="whatsapp" className="mt-4">
                <div className="bg-[#075E54] p-3 rounded-lg">
                  <div className="bg-[#DCF8C6] rounded-lg overflow-hidden max-w-[280px]">
                    {displayImageUrl && (
                      <img
                        src={displayImageUrl}
                        alt="Preview"
                        className="w-full h-36 object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <div className="p-2 bg-white border-l-4 border-[#34B7F1]">
                      <p className="text-sm font-semibold text-gray-900 line-clamp-2">
                        {displayTitle}
                      </p>
                      <p className="text-xs text-gray-600 line-clamp-2 mt-1">
                        {displayDescription}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {displayUrl}
                      </p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="linkedin" className="mt-4">
                <div className="border rounded-lg overflow-hidden bg-white">
                  {displayImageUrl && (
                    <img
                      src={displayImageUrl}
                      alt="Preview"
                      className="w-full h-40 object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div className="p-3 border-t bg-[#F3F2EF]">
                    <p className="text-sm font-semibold text-[#000000E6] line-clamp-2">
                      {displayTitle}
                    </p>
                    <p className="text-xs text-[#00000099] mt-1 line-clamp-2">
                      {displayDescription}
                    </p>
                    <p className="text-xs text-[#00000099] mt-1 flex items-center gap-1">
                      <Link className="w-3 h-3" />
                      {displayUrl}
                    </p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="twitter" className="mt-4">
                <div className="border rounded-xl overflow-hidden bg-white">
                  {socialPreview.twitter_card_type === 'summary_large_image' && displayImageUrl ? (
                    <img
                      src={displayImageUrl}
                      alt="Preview"
                      className="w-full h-40 object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : null}
                  <div className={`p-3 ${socialPreview.twitter_card_type === 'summary' ? 'flex gap-3' : ''}`}>
                    {socialPreview.twitter_card_type === 'summary' && displayImageUrl && (
                      <img
                        src={displayImageUrl}
                        alt="Preview"
                        className="w-20 h-20 object-cover rounded"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <div className="flex-1">
                      <p className="text-xs text-gray-500">
                        {displayUrl}
                      </p>
                      <p className="text-sm font-semibold text-gray-900 line-clamp-2 mt-0.5">
                        {displayTitle}
                      </p>
                      <p className="text-xs text-gray-600 line-clamp-2 mt-0.5">
                        {displayDescription}
                      </p>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <p className="text-xs text-muted-foreground text-center">
              This is an approximation. Actual appearance may vary by platform.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
